import { militaryStatesValues } from 'constants/enums/militaryStates';
import postOfficeCities from 'constants/enums/postOfficeCities';

import { isPO, isUS, isInternational } from 'helpers/location';
import { zipCodePattern, notPOBoxPattern, cityPattern } from 'constants/patterns';
import { countries } from 'config/locales/en/countries';
import { ValidateJSModel, LocationModel, ValidateJSContext } from 'forms/shared/types';
import { ValidateJSInclusionBaseValidatorOptions } from 'forms/shared/types/validatejs/inclusion';
import { REQUIRED } from 'constants/errors';
import { COUNTRY_FOREIGN, COUNTRY_POSTOFFICE } from 'constants/enums/locations';
import { countryValuesNoUS } from 'constants/enums/countries';
import { FormDetails } from 'util/singletons';
import { getUSInclusionsByForm, isPVQForm } from 'helpers/form';
import { hasYesOrNo } from 'models/validate';
import { isPostalZipcode } from 'config/zipcodes';

type CountryKeys = keyof typeof countries

const locationModelValidator: ValidateJSModel<LocationModel> = {
  countryComments: {},

  layout: {},

  street2: (value, attributes, attributeName, options) => {
    if (!options.allowPOBox) {
      return {
        presence: false,
        format: { pattern: notPOBoxPattern, flags: 'i', message: 'error.location.street.poBox.pattern' },
      };
    }

    return {
      presence: false,
    };
  },

  validated: {},

  street: (value, attributes, attributeName, options) => {
    if (options.allowPOBox) {
      return { presence: true };
    }

    return {
      presence: true,
      format: { pattern: notPOBoxPattern, flags: 'i', message: 'error.location.street.poBox.pattern' },
    };
  },

  city: (value, attributes, attributeName, options) => {
    const { requireCity = true } = options;
    if (isPO(attributes) || options.onlyMilitaryAddress === true) {
      return {
        presence: requireCity,
        inclusion: {
          within: postOfficeCities,
          message: 'error.city.apofporequired',
        },
      };
    }

    return {
      presence: requireCity,
      format: { pattern: cityPattern, message: 'error.city.pattern' },
      length: {
        minimum: 2,
        maximum: 100,
        message: 'error.city.length',
      },
    };
  },

  state: (value, attributes, attributeName, options) => {
    if (isPO(attributes) || options.onlyMilitaryAddress === true) {
      return {
        presence: true,
        inclusion: { within: militaryStatesValues, message: 'error.state.notfound' },
      };
    }

    if (isUS(attributes)) {
      return {
        presence: true,
        inclusion: {
          within: getUSInclusionsByForm(options?.formType),
          message: 'error.state.notfound',
        },
      };
    }

    return {};

    /**
     * The requireEmpty constraint was added to force certain fields to have no
     * value based on certain conditions. However, this broke some test data
     * since currently some data structures retain dead/unused data when values
     * are changed. For now all validation constraints are ignored for
     * irrelevant values, but this can be added back in the future.
     * See JIRA issue [EN-3928]
     * */
    // return { requireEmpty: true }
  },

  zipcode: (value, attributes) => {
    if (!isInternational(attributes)) {
      return {
        presence: true,
        format: { pattern: zipCodePattern, message: 'error.zipcode.pattern' },
        zipcode: true, // zipcode + state validator
      };
    }

    return {};

    /**
     * The requireEmpty constraint was added to force certain fields to have no
     * value based on certain conditions. However, this broke some test data
     * since currently some data structures retain dead/unused data when values
     * are changed. For now all validation constraints are ignored for
     * irrelevant values, but this can be added back in the future.
     * See JIRA issue [EN-3928]
     * */
    // return { requireEmpty: true }
  },

  country: (value, attributes, attributeName, options) => {
    if (attributes.countryFocused) {
      return {};
    }
    const inclusion: ValidateJSInclusionBaseValidatorOptions = options.excludeUnitedStates ? {
      within: countryValuesNoUS,
      message: 'error.country.notfound',
    } : {
      within: Object.keys(countries).map((key) => countries[key as CountryKeys].value),
      message: 'error.country.notfound',
    };

    const context: ValidateJSContext<any, any> = {
      presence: true,
      inclusion,
    };
    if (options.onlyMilitaryAddress === true) {
      inclusion.within = [COUNTRY_POSTOFFICE];
      inclusion.message = 'error.country.pomissing';

      return context;
    }
    // This handles the value 'INTERNATIONAL'. Basically, when this value is
    // set, the user is still required to input a country, thus, we return a
    // required error message here.
    if (typeof value === 'string' && value === COUNTRY_FOREIGN) { return { exclusion: { within: [COUNTRY_FOREIGN], message: REQUIRED } }; }

    if (typeof value === 'object' && value.value === COUNTRY_FOREIGN) {
      return {
        hasValue: {
          validator: {
            exclusion: { within: [COUNTRY_FOREIGN], message: REQUIRED },
          },
        },
      };
    }

    if (options.militaryAddress === true) {
      inclusion.within.push(COUNTRY_POSTOFFICE);

      return context;
    }

    if (options.militaryAddress === false) {
      context.exclusion = { within: [COUNTRY_POSTOFFICE], message: 'error.country.ponotallowed' };

      if (value === COUNTRY_POSTOFFICE) {
        context.inclusion = {};
      }

      return context;
    }

    return context;
  },

  county: (value, attributes) => {
    if (!isInternational(attributes)) {
      return { presence: true };
    }

    return {};

    /**
     * The requireEmpty constraint was added to force certain fields to have no
     * value based on certain conditions. However, this broke some test data
     * since currently some data structures retain dead/unused data when values
     * are changed. For now all validation constraints are ignored for
     * irrelevant values, but this can be added back in the future.
     * See JIRA issue [EN-3928]
     * */
    // return { requireEmpty: true }
  },

  apoFpoDpoPostcode: (value, attributes) => {
    const formDetails = FormDetails.getInstance().getData();
    if (!isPVQForm(formDetails.formType)) { return {}; }

    let validation = {};

    if (attributes.isMilitaryDiplomaticAddress === 'Yes' && isInternational(attributes)) {
      if (!isPostalZipcode(value) && value !== '') { // Check if zipcode is a valid APOFPODPO zipcode
        validation = {
          presence: true,
          format: { pattern: zipCodePattern, message: 'error.zipcode.pattern' },
          requireTrue: { // Force error message; value will never be true
            message: 'error.zipcode.state'
          }
        };
      } else {
        validation = {
          presence: true,
          format: { pattern: zipCodePattern, message: 'error.zipcode.pattern' },
        };
      }
    }
    return validation;
  },

  isMilitaryDiplomaticAddress: (value, attributes, attributeName, options) => {
    const formDetails = FormDetails.getInstance().getData();
    if (!isPVQForm(formDetails.formType)) { return {}; }

    return { presence: true, hasValue: { validator: hasYesOrNo } };
  },

  militaryDiplomaticInstallationName: (value, attributes, attributeName, options) => {
    const formDetails = FormDetails.getInstance().getData();
    if (!isPVQForm(formDetails.formType)) { return {}; }

    let validation = {};

    if (attributes.isMilitaryDiplomaticAddress === 'Yes'
      && attributes.militaryInstallationNameNotApplicable?.applicable !== false) {
      validation = { presence: true, hasValue: true };
    }
    return validation;
  },

  militaryInstallationNameNotApplicable: {},

  militaryDiplomaticInstallationNameExplanation: (value, attributes, attributeName, options) => {
    const formDetails = FormDetails.getInstance().getData();
    if (!isPVQForm(formDetails.formType)) { return {}; }

    let validation = {};

    if (isInternational(attributes)
      && attributes.isMilitaryDiplomaticAddress === 'Yes'
      && attributes.militaryInstallationNameNotApplicable?.applicable === false) {
      validation = { presence: true, hasValue: true };
    }
    return validation;
  },
};

export default locationModelValidator;
